import { useState } from "react";
import { BarChart3, MessageSquareText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { usePromptsStore } from "@/stores/prompts";
import type { PromptType } from "../../../types";
import { cn } from "@/lib/utils";

interface AddPromptFormProps {
  onSuccess?: () => void;
}

export function AddPromptForm({ onSuccess }: AddPromptFormProps) {
  const [type, setType] = useState<PromptType>("scoring");
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [template, setTemplate] = useState("");
  const [dimensions, setDimensions] = useState("");
  const [error, setError] = useState("");
  const add = usePromptsStore((s) => s.add);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim() || !template.trim()) {
      setError("Name and template are required");
      return;
    }

    let dims: string[];
    if (type === "sentiment") {
      dims = ["recommendation", "sentiment", "visibility"];
    } else {
      if (!dimensions.trim()) {
        setError("Dimensions are required for scoring prompts");
        return;
      }
      dims = dimensions.split(",").map((d) => d.trim()).filter(Boolean);
      if (dims.length === 0) {
        setError("At least one dimension is required");
        return;
      }
    }

    try {
      await add(name.trim(), template.trim(), dims, type, topic.trim());
      setName("");
      setTopic("");
      setTemplate("");
      setDimensions("");
      onSuccess?.();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>New Prompt</CardTitle>
        <CardDescription>
          Define how AI models should evaluate brands
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Type Selector */}
          <div>
            <Label>Type</Label>
            <div className="grid grid-cols-2 gap-2 mt-0.5">
              <button
                type="button"
                onClick={() => setType("scoring")}
                className={cn(
                  "flex items-center gap-3 rounded-md border p-3 text-left transition-all duration-150 cursor-pointer",
                  type === "scoring"
                    ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <BarChart3 className={cn("w-4 h-4 shrink-0", type === "scoring" ? "text-brand-600" : "text-slate-400")} />
                <div>
                  <p className={cn("text-[13px] font-semibold", type === "scoring" ? "text-brand-950" : "text-slate-600")}>
                    Scoring
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Structured scores across defined dimensions
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setType("sentiment")}
                className={cn(
                  "flex items-center gap-3 rounded-md border p-3 text-left transition-all duration-150 cursor-pointer",
                  type === "sentiment"
                    ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <MessageSquareText className={cn("w-4 h-4 shrink-0", type === "sentiment" ? "text-brand-600" : "text-slate-400")} />
                <div>
                  <p className={cn("text-[13px] font-semibold", type === "sentiment" ? "text-brand-950" : "text-slate-600")}>
                    Sentiment
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Natural language query with AI response analysis
                  </p>
                </div>
              </button>
            </div>
          </div>

          <div>
            <Label htmlFor="prompt-name">Name</Label>
            <Input
              id="prompt-name"
              placeholder={type === "sentiment" ? "e.g. Who should I call for mesothelioma help?" : "e.g. Brand Trust Assessment"}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <Label htmlFor="prompt-topic">Topic</Label>
            <Input
              id="prompt-topic"
              placeholder="e.g. Trust signals, local intent, pricing"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <p className="text-[11px] text-slate-400 mt-1.5">
              Used to group prompts in evaluation, study, and report selectors.
            </p>
          </div>

          <div>
            <Label htmlFor="prompt-template">
              {type === "sentiment" ? "Query" : "Template"}
            </Label>
            <Textarea
              id="prompt-template"
              rows={5}
              placeholder={
                type === "sentiment"
                  ? "Write a natural question a real person would ask. Use {brands} where you want the brand list inserted."
                  : "Use {brands} as a placeholder for the brand list. Request JSON output with scores and reasoning."
              }
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
            />
            {type === "sentiment" && (
              <p className="text-[11px] text-slate-400 mt-1.5">
                The AI will respond naturally to this query, then we analyze which brands it recommended and how favorably.
              </p>
            )}
          </div>

          {type === "scoring" && (
            <div>
              <Label htmlFor="prompt-dims">Dimensions</Label>
              <Input
                id="prompt-dims"
                placeholder="Comma-separated, e.g. reputation, expertise, trust"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}
              />
              <p className="text-[11px] text-slate-400 mt-1.5">
                Each dimension will be scored 0&ndash;100 by the AI model
              </p>
            </div>
          )}

          {type === "sentiment" && (
            <div className="rounded-md bg-slate-50 border border-slate-100 p-3">
              <p className="text-[11px] font-medium text-slate-500 mb-1">Auto-generated dimensions</p>
              <div className="flex gap-1.5">
                {["recommendation", "sentiment", "visibility"].map(d => (
                  <span key={d} className="inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-500">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Button type="submit">Create Prompt</Button>
          </div>
          {error && <p className="text-[13px] text-red-600">{error}</p>}
        </form>
      </CardContent>
    </Card>
  );
}
